import React, { useState, useMemo } from "react";

interface FloatingFeatureListProps {
  features: GeoJSON.Feature[];
  selectedFeature: GeoJSON.Feature | null;
  onFeatureSelect: (feature: GeoJSON.Feature) => void;
  isOpen: boolean;
  onClose: () => void;
}

interface LevelGroup {
  levelId: string;
  levelName: string;
  ordinal: number;
  features: GeoJSON.Feature[];
}

const FloatingFeatureList: React.FC<FloatingFeatureListProps> = ({
  features,
  selectedFeature,
  onFeatureSelect,
  isOpen,
  onClose,
}) => {
  // 用於追蹤哪些 level 是展開的
  const [expandedLevels, setExpandedLevels] = useState<Set<string>>(new Set());

  // 過濾只顯示 amenity 和 occupant
  const filteredFeatures = features.filter((feature) => {
    const fileType = feature.properties?._file_type;
    const sourceFile = feature.properties?._sourceFile;

    // 從檔案名或 _file_type 判斷類型
    let detectedType = fileType;
    if (!detectedType && sourceFile) {
      // 從檔案路徑推斷類型，只取檔案名部分
      const fileName = sourceFile.split("/").pop() || sourceFile;
      const baseName = fileName.replace(".geojson", "").toLowerCase();
      detectedType = baseName;
    }
    return detectedType === "amenity" || detectedType === "occupant";
  });

  // 輔助函數：獲取feature的類型
  const getFeatureType = (feature: GeoJSON.Feature): string => {
    const fileType = feature.properties?._file_type;
    const sourceFile = feature.properties?._sourceFile;

    if (fileType) return fileType;
    if (sourceFile) {
      // 從檔案路徑推斷類型，只取檔案名部分
      const fileName = sourceFile.split("/").pop() || sourceFile;
      return fileName.replace(".geojson", "").toLowerCase();
    }
    return "unknown";
  };

  // 輔助函數：檢查 feature 是否經過人工驗證
  const isManuallyVerified = (feature: GeoJSON.Feature): boolean => {
    if (!feature.properties) return false;

    const confirmURL = feature.properties.confirmURL;
    const stoConfirm = feature.properties.stoConfirm;

    // 驗證條件：confirmURL 存在且為字串，stoConfirm 為 true
    return (
      typeof confirmURL === "string" &&
      confirmURL.length > 0 &&
      stoConfirm === true
    );
  };

  // 建立按 level 分組的資料結構
  const groupedByLevel = useMemo(() => {
    // 首先建立 unit 和 level 的映射表
    const unitToLevel = new Map<
      string,
      { levelId: string; levelName: string; ordinal: number }
    >();

    // 找出所有的 unit 和 level features
    const unitFeatures = features.filter((f) => getFeatureType(f) === "unit");
    const levelFeatures = features.filter((f) => getFeatureType(f) === "level");

    // 建立 level 資訊映射
    const levelInfo = new Map<string, { name: string; ordinal: number }>();
    levelFeatures.forEach((level) => {
      const levelId = level.id || level.properties?.id;
      let levelName =
        level.properties?.name ||
        level.properties?.short_name ||
        levelId ||
        "未知樓層";

      // 如果 levelName 是物件（多語言），提取字串
      if (typeof levelName === "object" && levelName !== null) {
        levelName =
          levelName.en ||
          levelName.zh ||
          levelName["zh-Hant"] ||
          levelName["zh-TW"] ||
          Object.values(levelName)[0] ||
          "未知樓層";
      }

      // 確保是字串
      levelName = String(levelName);

      const ordinal = level.properties?.ordinal ?? 0;
      if (levelId) {
        levelInfo.set(levelId, { name: levelName, ordinal });
      }
    });

    // 建立 unit -> level 的映射
    unitFeatures.forEach((unit) => {
      const unitId = unit.id || unit.properties?.id;
      const levelId = unit.properties?.level_id;
      if (unitId && levelId) {
        const info = levelInfo.get(levelId);
        if (info) {
          unitToLevel.set(unitId, {
            levelId,
            levelName: info.name,
            ordinal: info.ordinal,
          });
        }
      }
    });

    // 將 amenity/occupant features 按 level 分組
    const levelGroups = new Map<string, LevelGroup>();

    filteredFeatures.forEach((feature) => {
      const featureType = getFeatureType(feature);
      let levelId = "unknown";
      let levelName = "未分類樓層";
      let ordinal = 9999;

      if (featureType === "amenity") {
        // amenity 使用 unit_ids (陣列)
        const unitIds = feature.properties?.unit_ids;
        if (Array.isArray(unitIds) && unitIds.length > 0) {
          // 使用第一個 unit 的 level
          const firstUnitId = unitIds[0];
          const levelData = unitToLevel.get(firstUnitId);
          if (levelData) {
            levelId = levelData.levelId;
            levelName = levelData.levelName;
            ordinal = levelData.ordinal;
          }
        }
      } else if (featureType === "occupant") {
        // occupant 使用 anchor_id -> 需要找到 anchor 的 unit_id -> level_id
        const anchorId = feature.properties?.anchor_id;
        if (anchorId) {
          // 找到對應的 anchor
          const anchor = features.find(
            (f) =>
              getFeatureType(f) === "anchor" &&
              (f.id || f.properties?.id) === anchorId
          );
          if (anchor) {
            const unitId = anchor.properties?.unit_id;
            if (unitId) {
              const levelData = unitToLevel.get(unitId);
              if (levelData) {
                levelId = levelData.levelId;
                levelName = levelData.levelName;
                ordinal = levelData.ordinal;
              }
            }
          }
        }
      }

      // 將 feature 加入對應的 level group
      if (!levelGroups.has(levelId)) {
        levelGroups.set(levelId, {
          levelId,
          levelName,
          ordinal,
          features: [],
        });
      }
      levelGroups.get(levelId)!.features.push(feature);
    });

    // 將 Map 轉換為陣列並排序
    return Array.from(levelGroups.values()).sort((a, b) => {
      // 先按 ordinal 排序，再按名稱排序
      if (a.ordinal !== b.ordinal) {
        return a.ordinal - b.ordinal;
      }
      // 確保 levelName 是字串再進行比較
      const nameA = String(a.levelName || "");
      const nameB = String(b.levelName || "");
      return nameA.localeCompare(nameB);
    });
  }, [features, filteredFeatures]);

  // 切換 level 的展開狀態
  const toggleLevel = (levelId: string) => {
    setExpandedLevels((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(levelId)) {
        newSet.delete(levelId);
      } else {
        newSet.add(levelId);
      }
      return newSet;
    });
  };

  const getFeatureDisplayName = (feature: GeoJSON.Feature): string => {
    const props = feature.properties;
    if (!props) return "未知項目";

    // 處理 name 屬性，它可能是字串或多語言物件
    if (props.name) {
      // 如果 name 是字串，直接返回
      if (typeof props.name === "string") {
        return props.name;
      }

      // 如果 name 是物件，嘗試取得適合的語言版本
      if (typeof props.name === "object" && props.name !== null) {
        const nameValue =
          props.name.en ||
          props.name.zh ||
          props.name["zh-Hant"] ||
          props.name["zh-TW"] ||
          Object.values(props.name)[0];
        // 確保返回字串
        if (nameValue && typeof nameValue === "string") {
          return nameValue;
        }
      }
    }

    return String(props.id || "未命名項目");
  };

  const FeatureItem: React.FC<{ feature: GeoJSON.Feature }> = ({ feature }) => {
    const isSelected =
      (selectedFeature?.id || selectedFeature?.properties?.id) ===
      (feature.id || feature.properties?.id);
    const isVerified = isManuallyVerified(feature);

    return (
      <div
        className={`p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
          isSelected ? "bg-blue-50 border-l-4 border-l-blue-500" : ""
        }`}
        onClick={() => onFeatureSelect(feature)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">
              {getFeatureDisplayName(feature)}
            </div>
            <div className="text-xs text-gray-500 space-y-1">
              <div>ID: {feature.id || feature.properties?.id}</div>
              {isVerified ? (
                <div className="flex items-center text-green-600">
                  <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  已完成人工驗證
                </div>
              ) : (
                <div className="flex items-center text-yellow-600">
                  <span className="inline-block w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                  待人工驗證
                </div>
              )}
            </div>
          </div>
          {isSelected && (
            <div className="text-blue-500">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* 背景遮罩 */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-20 transition-opacity duration-300"
          style={{ zIndex: 10000 }}
          onClick={onClose}
        />
      )}

      {/* 浮動面板 */}
      <div
        className={`fixed left-0 top-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ zIndex: 10001 }}
      >
        {/* 標題列與關閉按鈕 */}
        <div className="p-4 bg-white border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Features</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="關閉列表"
          >
            <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {/* 統計資訊 */}
        <div className="px-4 py-2 bg-gray-50 border-b">
          <p className="text-sm text-gray-600">
            {filteredFeatures.length} 個可編輯項目，按樓層分類
          </p>
        </div>

        {/* 內容區域 */}
        <div className="h-full overflow-y-auto pb-40">
          {" "}
          {/* pb-40 預留底部空間 */}
          <div className="bg-white">
            {/* 按樓層分組的樹狀結構 */}
            {groupedByLevel.length > 0 ? (
              groupedByLevel.map((levelGroup) => {
                const isExpanded = expandedLevels.has(levelGroup.levelId);
                const amenityCount = levelGroup.features.filter(
                  (f) => getFeatureType(f) === "amenity"
                ).length;
                const occupantCount = levelGroup.features.filter(
                  (f) => getFeatureType(f) === "occupant"
                ).length;
                const unconfirmedCount = levelGroup.features.filter(
                  (f) => f.properties?.stoConfirm !== true
                ).length;

                return (
                  <div key={levelGroup.levelId}>
                    {/* Level 標題 - 可展開/收合 */}
                    <div
                      className="px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100 border-b cursor-pointer hover:from-blue-100 hover:to-blue-200 transition-colors"
                      onClick={() => toggleLevel(levelGroup.levelId)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <svg
                            className={`w-4 h-4 mr-2 transition-transform ${
                              isExpanded ? "rotate-90" : ""
                            }`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <h4 className="text-sm font-semibold text-gray-800">
                            {levelGroup.levelName}
                            <span className="ml-2 text-xs font-normal text-gray-600">
                              (未確認: {unconfirmedCount})
                            </span>
                          </h4>
                        </div>
                        <div className="text-xs text-gray-600">
                          <span className="inline-block px-2 py-1 bg-white rounded mr-1">
                            Amenity: {amenityCount}
                          </span>
                          <span className="inline-block px-2 py-1 bg-white rounded">
                            Occupant: {occupantCount}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Level 內的 Features */}
                    {isExpanded && (
                      <div className="bg-white">
                        {levelGroup.features.map((feature, index) => (
                          <FeatureItem
                            key={`${levelGroup.levelId}-${
                              feature.properties?.id || index
                            }`}
                            feature={feature}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              /* 沒有可編輯項目時的提示 */
              <div className="p-8 text-center text-gray-500">
                <div className="mb-2">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 48 48"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M20 20l4-4m0 0l4 4m-4-4v12"
                    />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-1">
                  沒有可編輯的項目
                </h4>
                <p className="text-sm text-gray-500">
                  此 IMDF 檔案中沒有包含 Amenity 或 Occupant 項目
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FloatingFeatureList;
